import { flatten } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from 'components/ConfirmationModal';
import { Spinner, useMessage } from 'components/ui';
import { formatDamageId } from 'horizon/components/Damages/utils';
import {
  type AtlasGqlHorizonDamage,
  useGetHorizonDamagesForMergingQuery,
  useMergeHorizonDamagesMutation,
} from 'types/atlas-graphql';

const POLL_INTERVAL = 2000;

export type MergeConfirmationState = {
  isOpen: boolean;
  targetDamageId?: string;
  sourceDamageIds?: string[];
};

type MergeDamagesModalProps = {
  mergeConfirmationState: MergeConfirmationState;
  onClose: () => void;
  onSuccess?: (damage: AtlasGqlHorizonDamage) => void;
  onError?: (error: Error) => void;
  refetchQueries?: string[];
};

export const MergeDamagesModal: React.FunctionComponent<MergeDamagesModalProps> = ({
  mergeConfirmationState,
  onClose,
  onSuccess,
  onError,
  refetchQueries = [],
}) => {
  const { t } = useTranslation();
  const message = useMessage();
  const [observationCount, setObservationCount] = useState<number>();
  const [taskCount, setTaskCount] = useState<number>();
  const [commentCount, setCommentCount] = useState<number>();
  const [isPolling, setIsPolling] = useState<boolean>(false);
  const [pollingCount, setPollingCount] = useState<number>(0);

  const { isOpen, targetDamageId, sourceDamageIds = [] } = mergeConfirmationState;

  const damageIdsToMerge = [targetDamageId, ...sourceDamageIds];

  // query for damages to be merged together (for validation)
  const { data: damagesData, loading: damagesLoading } = useGetHorizonDamagesForMergingQuery({
    variables: { input: { ids: damageIdsToMerge }, limit: damageIdsToMerge.length },
    fetchPolicy: 'network-only',
    skip: !isOpen || damageIdsToMerge.length === 0,
  });

  useEffect(() => {
    if (!damagesData) {
      return;
    }
    const damages = damagesData?.getHorizonDamages?.items;

    if (damages) {
      const observations = flatten(
        flatten(damages.map(d => d?.observationGroups?.map(g => g?.observations ?? [])))
      );
      const tasks = flatten(damages.map(d => d?.tasks));
      const comments = flatten(
        flatten(damages.map(d => d?.observationGroups?.map(g => g?.comments ?? [])))
      );

      setObservationCount(observations.length);
      setTaskCount(tasks.length);
      setCommentCount(comments.length);
    }
  }, [damagesData]);

  // query for final merged damage (for validation)
  const {
    data: pollingData,
    startPolling,
    stopPolling,
  } = useGetHorizonDamagesForMergingQuery({
    variables: { input: { ids: [targetDamageId] }, limit: 1 },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: !isPolling || !targetDamageId,
  });

  // merge damage mutation
  const [mergeDamages, { loading: mergeLoading }] = useMergeHorizonDamagesMutation({
    onCompleted: data => {
      const damage = data.mergeHorizonDamages;

      // check that the tasks & comments have been properly updated
      validateMerge(damage as AtlasGqlHorizonDamage | undefined);
    },
    onError: error => {
      handleError(error);
    },
    refetchQueries: ['GetComments', ...refetchQueries],
  });

  // success handler
  const handleSuccess = useCallback(
    (damage: AtlasGqlHorizonDamage) => {
      message.success(
        <>
          {t('damage.damageLinkedSuccess', 'Damage successfully linked to Damage')}{' '}
          <a href={`/damages2/${damage.id}`} target="_blank" rel="noreferrer">
            {formatDamageId({ id: damage.id })}
          </a>
        </>
      );

      onSuccess && onSuccess(damage);
      onClose();
    },
    [message, onSuccess, onClose, t]
  );

  // error handler
  const handleError = useCallback(
    (error: Error) => {
      message.error(
        t('damage.errorLinkingDamages', 'There was an error linking the damages. Please try again.')
      );

      onError && onError(error);
      onClose();
    },
    [message, onError, onClose, t]
  );

  // checks that the tasks & comments have been properly updated
  const validateMerge = useCallback(
    (damage?: AtlasGqlHorizonDamage) => {
      if (!!damage) {
        const tasks = damage.tasks ?? [];
        const comments = flatten([...(damage.observationGroups ?? []).map(g => g?.comments ?? [])]);

        if ((tasks.length === taskCount && comments.length === commentCount) || pollingCount > 3) {
          stopPolling();
          setIsPolling(false);
          handleSuccess(damage);
        } else {
          startPolling(POLL_INTERVAL);
          setIsPolling(true);
        }
      }
    },
    [startPolling, stopPolling, pollingCount, taskCount, commentCount, handleSuccess]
  );

  useEffect(() => {
    if (!pollingData || !isPolling) {
      return;
    }
    const damage = pollingData?.getHorizonDamages?.items?.[0];

    // check that the tasks & comments have been properly updated
    validateMerge(damage as AtlasGqlHorizonDamage | undefined);

    // increment polling count
    setPollingCount(count => count + 1);
  }, [pollingData, isPolling, validateMerge]);

  return (
    <ConfirmationModal
      open={isOpen}
      loading={damagesLoading || mergeLoading || isPolling}
      title={t('damage.confirmDamageLink', 'Confirm Damage Link')}
      okText={t('damage.link', 'Link')}
      onOk={() => {
        if (!!targetDamageId && !!sourceDamageIds) {
          mergeDamages({
            variables: {
              input: {
                targetDamageId,
                sourceDamageIds,
              },
            },
          });
        }
      }}
      cancelText={t('common.cancel', 'Cancel')}
      onCancel={onClose}
      message={
        damagesLoading ? (
          <Spinner />
        ) : (
          t(
            'damage.mergeDamagesWarning',
            'This action will link {{damageCount}} Damages, with {{observationCount}} Observations and all associated Tasks and Comments, together under Damage ID {{damageId}}. This action cannot be undone.',
            {
              damageCount: damageIdsToMerge.length,
              observationCount,
              damageId: formatDamageId({ id: targetDamageId }),
            }
          )
        )
      }
      destroyOnHidden
    />
  );
};
